'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Skeleton, Spinner, Tab } from '@common/components';
import SvgChevronright from '@common/icon/SvgChevronright';
import SvgRefresh from '@common/icon/SvgRefresh';
import { cn } from '@core/utils';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { usePathname, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { CustomLink } from '@/app/_components/CustomLink';
import { DONATION_ROUTES } from '@/constants';
import { buildCampaignListOptions, CAMPAIGN_PHASE_SEARCH_PARAM, type CampaignPhaseTab, toCampaignPhaseTab } from '../_utils/campaignListQuery';
import { useRowFade } from '../_utils/motion';
import { CAMPAIGN_PAGE_SIZE } from '../constants';
import { CampaignCard } from './CampaignCard';
const PHASE_TAB_LIST = [{
  value: 'RECRUITING',
  label: '모집중'
}, {
  value: 'UPCOMING',
  label: '모집예정'
}, {
  value: 'CLOSED',
  label: '모집완료'
}] as const;
const LIST_CLASS_NAME = cn('flex flex-col gap-20 pl-8', 'mobile-tablet:grid mobile-tablet:grid-cols-2 mobile-tablet:gap-16 mobile-tablet:pl-0');
const SKELETON_CARD_COUNT = 6;
const NOTICE_CLASS_NAME = 'flex flex-col items-center gap-16 py-160 text-center text-Gray-550 typo-body3';
const CampaignListSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ul aria-hidden={true} className={LIST_CLASS_NAME}>{Array.from({
        length: SKELETON_CARD_COUNT
      }).map(_temp)}</ul>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const CampaignList = () => {
  const $ = _c(54);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const phaseTab = toCampaignPhaseTab(searchParams.get(CAMPAIGN_PHASE_SEARCH_PARAM));
  const [isTabSwitched, setIsTabSwitched] = useState(false);
  const rowFade = useRowFade();
  const t0 = buildCampaignListOptions(phaseTab);
  let t1;
  if ($[0] !== t0) {
    t1 = {
      ...t0,
      placeholderData: keepPreviousData
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isPlaceholderData
  } = useInfiniteQuery(t1);
  let t2;
  if ($[2] !== fetchNextPage || $[3] !== hasNextPage || $[4] !== isFetchingNextPage) {
    t2 = {
      onChange: inView => {
        if (inView && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    };
    $[2] = fetchNextPage;
    $[3] = hasNextPage;
    $[4] = isFetchingNextPage;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    ref: loadMoreRef
  } = useInView(t2);
  const firstPage = data?.pages[0]?.context;
  let t3;
  if ($[6] !== firstPage) {
    t3 = firstPage ? {
      RECRUITING: firstPage.recruitingCampaignCount,
      UPCOMING: firstPage.upcomingCampaignCount,
      CLOSED: firstPage.closedCampaignCount
    } : undefined;
    $[6] = firstPage;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const countByPhase = t3;
  let t4;
  if ($[8] !== data?.pages || $[9] !== firstPage || $[10] !== hasNextPage || $[11] !== isError || $[12] !== isPlaceholderData || $[13] !== isTabSwitched || $[14] !== loadMoreRef || $[15] !== refetch || $[16] !== rowFade) {
    const campaignList = data?.pages.flatMap(_temp2) ?? [];
    bb0: {
      if (!firstPage) {
        if (isError) {
          let t5;
          if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
            t5 = <p>체험단 목록을 불러오지 못했어요.</p>;
            $[18] = t5;
          } else {
            t5 = $[18];
          }
          let t6;
          if ($[19] !== refetch) {
            t6 = () => refetch();
            $[19] = refetch;
            $[20] = t6;
          } else {
            t6 = $[20];
          }
          let t7;
          if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
            t7 = <SvgRefresh className="size-16" />;
            $[21] = t7;
          } else {
            t7 = $[21];
          }
          let t8;
          if ($[22] !== t6) {
            t8 = <div className={NOTICE_CLASS_NAME}>{t5}<Button theme="sub-line" dimensions="H48" onClick={t6}>{t7}다시 시도</Button></div>;
            $[22] = t6;
            $[23] = t8;
          } else {
            t8 = $[23];
          }
          t4 = t8;
          break bb0;
        }
        let t5;
        if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = <CampaignListSkeleton />;
          $[24] = t5;
        } else {
          t5 = $[24];
        }
        t4 = t5;
        break bb0;
      }
      if (campaignList.length === 0 && !isPlaceholderData) {
        let t5;
        if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = <p>지금은 등록된 체험단이 없어요.</p>;
          $[25] = t5;
        } else {
          t5 = $[25];
        }
        let t6;
        if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <div className={NOTICE_CLASS_NAME}>{t5}<Button theme="sub-line" dimensions="H48" asChild={true}><CustomLink href={DONATION_ROUTES.GIFTS.pathname}>답례품 보러가기<SvgChevronright className="size-16" /></CustomLink></Button></div>;
          $[26] = t6;
        } else {
          t6 = $[26];
        }
        t4 = t6;
        break bb0;
      }
      const t5 = isPlaceholderData && "opacity-50";
      let t6;
      if ($[27] !== t5) {
        t6 = cn(LIST_CLASS_NAME, "transition-opacity duration-200", t5);
        $[27] = t5;
        $[28] = t6;
      } else {
        t6 = $[28];
      }
      let t7;
      if ($[29] !== isTabSwitched || $[30] !== rowFade) {
        t7 = (campaign, index) => {
          const fade = rowFade(index);
          return <motion.li key={campaign.campaignId} initial={isTabSwitched && index < CAMPAIGN_PAGE_SIZE ? fade.initial : false} animate={fade.animate}><CampaignCard campaign={campaign} /></motion.li>;
        };
        $[29] = isTabSwitched;
        $[30] = rowFade;
        $[31] = t7;
      } else {
        t7 = $[31];
      }
      const t8 = <ul className={t6}>{campaignList.map(t7)}</ul>;
      let t9;
      if ($[32] !== hasNextPage || $[33] !== loadMoreRef) {
        t9 = hasNextPage ? <div ref={loadMoreRef} className="py-24 flex-center"><Spinner /></div> : null;
        $[32] = hasNextPage;
        $[33] = loadMoreRef;
        $[34] = t9;
      } else {
        t9 = $[34];
      }
      let t10;
      if ($[35] !== t8 || $[36] !== t9) {
        t10 = <>{t8}{t9}</>;
        $[35] = t8;
        $[36] = t9;
        $[37] = t10;
      } else {
        t10 = $[37];
      }
      t4 = t10;
    }
    $[8] = data?.pages;
    $[9] = firstPage;
    $[10] = hasNextPage;
    $[11] = isError;
    $[12] = isPlaceholderData;
    $[13] = isTabSwitched;
    $[14] = loadMoreRef;
    $[15] = refetch;
    $[16] = rowFade;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const content = t4;
  let t5;
  if ($[38] !== pathname || $[39] !== searchParams || $[40] !== setIsTabSwitched) {
    t5 = nextTab => {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set(CAMPAIGN_PHASE_SEARCH_PARAM, nextTab);
      history.replaceState({
        historyIndex: history.state?.historyIndex
      }, "", `${pathname}?${nextSearchParams}`);
      setIsTabSwitched(true);
    };
    $[38] = pathname;
    $[39] = searchParams;
    $[40] = setIsTabSwitched;
    $[41] = t5;
  } else {
    t5 = $[41];
  }
  let t6;
  if ($[42] !== countByPhase) {
    t6 = PHASE_TAB_LIST.map(phase => <Tab.Item key={phase.value} value={phase.value}>{countByPhase ? `${phase.label} ${countByPhase[phase.value]}` : phase.label}</Tab.Item>);
    $[42] = countByPhase;
    $[43] = t6;
  } else {
    t6 = $[43];
  }
  let t7;
  if ($[44] !== phaseTab || $[45] !== t5 || $[46] !== t6) {
    t7 = <Tab value={phaseTab} onChange={t5} aria-label={"\uCCB4\uD5D8\uB2E8 \uBAA8\uC9D1 \uC0C1\uD0DC"}>{t6}</Tab>;
    $[44] = phaseTab;
    $[45] = t5;
    $[46] = t6;
    $[47] = t7;
  } else {
    t7 = $[47];
  }
  let t8;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn("pb-60 pt-16", "mobile-tablet:px-20 mobile-tablet:pb-28 mobile-tablet:pt-24");
    $[48] = t8;
  } else {
    t8 = $[48];
  }
  let t9;
  if ($[49] !== content) {
    t9 = <div className={t8}>{content}</div>;
    $[49] = content;
    $[50] = t9;
  } else {
    t9 = $[50];
  }
  let t10;
  if ($[51] !== t7 || $[52] !== t9) {
    t10 = <section aria-label={"\uCCB4\uD5D8\uB2E8 \uBAA9\uB85D"} className="flex flex-col">{t7}{t9}</section>;
    $[51] = t7;
    $[52] = t9;
    $[53] = t10;
  } else {
    t10 = $[53];
  }
  return t10;
};
function _temp(_, index) {
  return <li key={index} className={cn("flex items-center gap-16", "mobile-tablet:flex-col mobile-tablet:items-stretch mobile-tablet:gap-12")}><Skeleton className={cn("size-160 shrink-0 rounded-small", "mobile-tablet:aspect-square mobile-tablet:size-auto mobile-tablet:rounded-large")} /><div className="flex w-full min-w-0 flex-1 flex-col gap-8"><Skeleton typo="caption1" className="w-1/3 rounded-xxsmall" /><Skeleton typo="body4" className="w-full rounded-xxsmall" /><Skeleton typo="caption1" className="w-2/3 rounded-xxsmall" /></div></li>;
}
function _temp2(page) {
  return page.context?.contents ?? [];
}